import { useEffect, useRef } from "react";
import { Socket } from "socket.io-client";
import toast from "react-hot-toast";
import { useAppSelector } from "../hooks";
import socket from "../utils/socket";
import { Member } from "../interfaces";
import { Message } from "../interfaces/chat.interface";

interface NotificationOption {
  showToast?: boolean;
  muteSound?: boolean;
  showBrowserNotification?: boolean;
}

export default function useNotificationSocket(options: NotificationOption, onMessage?: (msg: Message) => void) {
  const socketRef = useRef<Socket | null>(null);
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    if (!socketRef.current) {
      socketRef.current = socket;

      socketRef.current.on("connect", () => {
        console.log("notification socket connected", socketRef.current?.id);
      });

      socketRef.current.on("disconnect", () => {
        console.log("notification socket disconnected");
      });
    }

    if (!user || !socket) return;

    // Request browser permission once
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "default") {
        Notification.requestPermission();
      }
    }

    const handler = (msg: any) => {
      if (canReceive(msg, user)) {
        if (options.showToast) showToastNotification(msg);
        if (!options.muteSound) playSound();
        if (options.showBrowserNotification) showBrowserNotification(msg);
        if (onMessage) onMessage(msg);
      }
    };

    socket.on("message:notification", handler);

    return () => {
      socket.off("message:notification", handler);
    };
  }, [user]);
}

// Role-based filter
function canReceive(msg: Message, user: Member) {
  if (msg.admin?.id === user.id) return false;

  if (user.role === "SUPER_ADMIN") return true;
  const batchIds = user?.assigned_batches?.map((b) => b.batch_id);
  return batchIds?.includes(msg.chat?.batch_id!);
}

// 🔔 UI Toast
function showToastNotification(msg: any) {
  toast(`${msg.admin?.name || msg.student?.name}: ${msg.message}`, {
    duration: 4000,
    icon: "💬",
  });
}

// 🔊 Sound
function playSound() {
  const audio = new Audio("/sounds/notify.wav");
  audio.play().catch(() => {});
}

// 🖥️ Browser Push Notification
function showBrowserNotification(msg: any) {
  if (typeof window !== "undefined" && "Notification" in window) {
    // ✅ Safe to use Notification API
    if (Notification.permission === "granted") {
      new Notification("You have a new message!", {
        body: msg.message,
        icon: "/logo192.png", // or your favicon
      });
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          new Notification("You have a new message!", {
            body: msg.message,
            icon: "/logo192.png", // or your favicon
          });
        }
      });
    }
  } else {
    console.log("Notifications are not supported in this browser.");
  }
}
